import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { NovelService, Novel } from '../../services/novelService';
import PageHeader from '../common/PageHeader';
import './PlotEditor.css';

interface PlotEditorProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const PlotEditor: React.FC<PlotEditorProps> = ({ user, onClose, onToggleSidebar }) => {
  const { novelId, plotIndex } = useParams<{ novelId: string; plotIndex: string }>();
  const navigate = useNavigate();
  
  const [novel, setNovel] = useState<Novel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [plotContent, setPlotContent] = useState('');
  const [syncStatus, setSyncStatus] = useState<'saved' | 'saving' | 'error'>('saved');
  const [enhancing, setEnhancing] = useState(false);

  const index = plotIndex ? parseInt(plotIndex, 10) : 0;

  useEffect(() => {
    if (novelId) {
      loadNovel();
    }
  }, [novelId]);

  const loadNovel = async () => {
    if (!novelId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await NovelService.getNovel(novelId);
      setNovel(data);
      
      const plots = data.metadata?.plots || [];
      if (index >= 0 && index < plots.length) {
        setPlotContent(plots[index]);
      }
    } catch (err) {
      console.error('Error loading novel:', err);
      setError('Failed to load novel');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/novel/${novelId}/metadata/plots`);
  };

  const handleSave = async () => {
    if (!novelId || !novel) return;

    try {
      setSyncStatus('saving');
      const plots = [...(novel.metadata?.plots || [])];
      plots[index] = plotContent;
      
      const metadata = { ...novel.metadata, plots };
      const updated = await NovelService.updateNovelMetadata(novelId, novel.genre || '', metadata);
      setNovel(updated);
      setSyncStatus('saved');
      setError(null);
    } catch (err) {
      console.error('Error saving plot:', err);
      setSyncStatus('error');
      setError('Failed to save plot');
    }
  };

  const handleEnhance = async () => {
    if (!novelId || !plotContent) return;

    setEnhancing(true);
    try {
      const enhanced = await NovelService.enhanceMetadataField(
        novelId,
        'plot',
        plotContent
      );
      setPlotContent(enhanced);
    } catch (err: any) {
      console.error('Error enhancing plot:', err);
      setError('Failed to enhance plot');
    } finally {
      setEnhancing(false);
    }
  };

  const handleDelete = async () => {
    if (!novelId || !novel) return;
    if (!window.confirm('Are you sure you want to delete this plot?')) return;

    try {
      setSyncStatus('saving');
      const plots = [...(novel.metadata?.plots || [])];
      plots.splice(index, 1);
      
      const metadata = { ...novel.metadata, plots };
      await NovelService.updateNovelMetadata(novelId, novel.genre || '', metadata);
      navigate(`/novel/${novelId}/metadata/plots`);
    } catch (err) {
      console.error('Error deleting plot:', err);
      setError('Failed to delete plot');
      setSyncStatus('error');
    }
  };

  if (loading) {
    return (
      <div className="plot-editor-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Loading...</h1>}
        />
        <div className="plot-editor-loading">Loading plot...</div>
      </div>
    );
  }

  if (error && !novel) {
    return (
      <div className="plot-editor-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Error</h1>}
        />
        <div className="plot-editor-error">
          {error || 'Novel not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="plot-editor-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <div className="plot-editor-header-center">
            <h1 className="page-header-center-title">
              {novel?.title} /plot #{index + 1}
            </h1>
            <span className={`plot-sync-status ${syncStatus}`}>
              {syncStatus === 'saving' && '⟳ Saving...'}
              {syncStatus === 'saved' && '✓ Saved'}
              {syncStatus === 'error' && '✕ Save failed'}
            </span>
          </div>
        }
        leftActions={
          <button 
            className="plot-editor-back-btn"
            onClick={handleBack}
          >
            ← Back to Plots
          </button>
        }
        rightActions={
          <div className="plot-editor-header-actions">
            <button 
              className="plot-editor-enhance-btn"
              onClick={handleEnhance}
              disabled={!plotContent || enhancing}
              title="Enhance with AI"
            >
              {enhancing ? '⟳' : '✨'} Enhance
            </button>
            <button 
              className="plot-editor-save-btn"
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        }
      />

      <div className="plot-editor-content">
        <div className="plot-editor-main">
          <textarea
            className="plot-editor-textarea"
            value={plotContent}
            onChange={(e) => setPlotContent(e.target.value)}
            placeholder="Describe the plot in detail..."
          />
        </div>

        {error && (
          <div className="plot-editor-error-message">
            {error}
          </div>
        )}

        <div className="plot-editor-footer">
          <button
            className="plot-editor-delete-btn"
            onClick={handleDelete}
          >
            Delete Plot
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlotEditor;

